import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import {
  alertSchema,
  type Alert,
  type AlertCondition,
  type AlertDraft,
  type AlertKind,
  type AlertTab,
} from '@/schemas/alerts'
import { lenientList } from '@/schemas/common'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * Every `alert_type` mm-be serves, both ways. Total on both sides, so a type
 * the spec adds fails typecheck here: "Set it again" on an RSI alert posts
 * RSI, never the VOLUME a price-or-volume ternary would have sent.
 */
const KIND: Readonly<Record<Wire<'AlertTypeEnum'>, AlertKind>> = {
  PRICE: 'price',
  VOLUME: 'volume',
  CHANGE: 'change',
  RSI: 'rsi',
  MACD: 'macd',
  MA: 'ma',
}
const WIRE_TYPE: Readonly<Record<AlertKind, Wire<'AlertTypeEnum'>>> = {
  price: 'PRICE',
  volume: 'VOLUME',
  change: 'CHANGE',
  rsi: 'RSI',
  macd: 'MACD',
  ma: 'MA',
}
const CONDITION: Record<string, AlertCondition> = { UP: 'above', DOWN: 'below' }

const isWireType = (value: string): value is Wire<'AlertTypeEnum'> => Object.hasOwn(KIND, value)

/**
 * `alert_type` as mm-be reads it. Legacy rows predate the column and carry
 * NULL, which every mm-be path treats as PRICE (services/alerts/evaluation.py
 * and `_to_view` test only `== VOLUME`). The spec types it `string`, so the
 * parameter admits the null rather than assuming it away — one legacy alert
 * must not blank the whole list. A type this build does not know passes
 * through untouched, so the list drops that one row with a warning instead of
 * showing it as a price alert.
 */
const kindOf = (alertType: string | null): string =>
  alertType === null ? 'price' : isWireType(alertType) ? KIND[alertType] : alertType

const toWireType = (kind: AlertKind): Wire<'AlertTypeEnum'> => WIRE_TYPE[kind]
const toWireDirection = (condition: AlertCondition): Wire<'DirectionEnum'> =>
  condition === 'above' ? 'UP' : 'DOWN'

/**
 * Wire → app, before the parse. `alert_type`/`direction` are plain strings in
 * the OpenAPI response, so an unknown value is passed through untouched and
 * the Zod enum rejects it at the boundary rather than a component guessing.
 */
const alertFields = (wire: Wire<'Alert'>) => ({
  id: wire.id,
  kind: kindOf(wire.alert_type),
  condition: CONDITION[wire.direction] ?? wire.direction,
  symbol: wire.symbol,
  company: wire.company_name,
  companyId: wire.company_id,
  name: wire.name,
  trigger: wire.trigger,
  lastTraded: wire.last_traded,
  lastVolume: wire.last_volume,
  lastChangePercent: wire.last_change_percent,
  toGoPercent: wire.to_go_percent,
  channels: wire.channels,
  expiresOn: wire.expires_on,
  status: wire.status,
  firedAt: wire.fired_at,
  firedPrice: wire.fired_price,
  message: wire.message,
})

export const mapAlert = (wire: Wire<'Alert'>): Alert => alertSchema.parse(alertFields(wire))

/**
 * The list row by row: one alert set from another client in a shape this
 * build cannot read costs that row, not the Alerts page, the chart rail's
 * block and the fired-alert poll that all read this list.
 */
const alertListSchema = lenientList(alertSchema, 'alert')

const blankToNull = (name: string): string | null => (name.trim() === '' ? null : name.trim())

/**
 * The level on the wire. MACD crosses its own signal line and has none: mm-be ignores a value
 * sent with it, so none is sent. Every other type is refused (`ALERT_INVALID`) without one.
 */
const levelOf = (draft: AlertDraft): { trigger?: string } =>
  draft.kind === 'macd' ? {} : { trigger: draft.trigger }

/**
 * Only the fields that differ from the saved alert.
 *
 * mm-be re-arms a fired alert whenever a PATCH carries trigger, direction,
 * type or expiry — present, not changed — so sending the whole draft would
 * silently re-arm on a mere rename.
 */
export const alertPatch = (saved: Alert, draft: AlertDraft): Wire<'PatchedAlertUpdateRequest'> => {
  const patch: Wire<'PatchedAlertUpdateRequest'> = {}
  if (draft.kind !== saved.kind) patch.alert_type = toWireType(draft.kind)
  if (draft.condition !== saved.condition) patch.direction = toWireDirection(draft.condition)
  // A new type needs a level of its own: the saved one was checked against the old type's rules
  // (a rupee price is no RSI level), so it goes along even when the number is the same.
  if (
    draft.kind !== 'macd' &&
    (draft.kind !== saved.kind || Number(draft.trigger) !== Number(saved.trigger))
  ) {
    patch.trigger = draft.trigger
  }
  if (draft.expiresOn !== saved.expiresOn) patch.expires_on = draft.expiresOn
  const app = draft.channels.includes('app')
  const email = draft.channels.includes('email')
  if (app !== saved.channels.includes('app')) patch.notify_on_app = app
  if (email !== saved.channels.includes('email')) patch.send_email = email
  const name = blankToNull(draft.name)
  if (name !== saved.name) patch.name = name
  return patch
}

export const alertsService = {
  /** One tab: `active` = not past expiry (ACTIVE, FIRED, PAUSED), else expired. */
  list: async (tab: AlertTab): Promise<Alert[]> => {
    const page = await getPage<Wire<'Alert'>>(() =>
      apiClient.GET('/api/v5/alerts/', { params: { query: { status: tab } } }),
    )
    return alertListSchema.parse(page.rows.map(alertFields))
  },

  create: async (draft: AlertDraft): Promise<Alert> =>
    mapAlert(
      await getData<Wire<'Alert'>>(() =>
        apiClient.POST('/api/v5/alerts/', {
          body: {
            symbol: draft.symbol,
            alert_type: toWireType(draft.kind),
            direction: toWireDirection(draft.condition),
            ...levelOf(draft),
            expires_on: draft.expiresOn,
            notify_on_app: draft.channels.includes('app'),
            send_email: draft.channels.includes('email'),
            name: blankToNull(draft.name),
          },
        }),
      ),
    ),

  update: async (saved: Alert, draft: AlertDraft): Promise<Alert> =>
    mapAlert(
      await getData<Wire<'Alert'>>(() =>
        apiClient.PATCH('/api/v5/alerts/{alert_id}/', {
          params: { path: { alert_id: saved.id } },
          body: alertPatch(saved, draft),
        }),
      ),
    ),

  /**
   * Pause (`false`) or resume (`true`). Pausing is never plan-gated; resuming counts against the
   * armed cap like a re-arm, and leaves a fired alert fired.
   */
  setActive: async (id: number, active: boolean): Promise<Alert> =>
    mapAlert(
      await getData<Wire<'Alert'>>(() =>
        apiClient.PATCH('/api/v5/alerts/{alert_id}/', {
          params: { path: { alert_id: id } },
          body: { active },
        }),
      ),
    ),

  remove: async (id: number): Promise<void> => {
    await getData<Wire<'AlertDeletedResponse'>>(() =>
      apiClient.DELETE('/api/v5/alerts/{alert_id}/', { params: { path: { alert_id: id } } }),
    )
  },

  /** "Set again" on a fired card. 409 `ALERT_EXPIRED` once past expiry. */
  rearm: async (id: number): Promise<Alert> =>
    mapAlert(
      await getData<Wire<'Alert'>>(() =>
        apiClient.POST('/api/v5/alerts/{alert_id}/rearm/', {
          params: { path: { alert_id: id } },
        }),
      ),
    ),
}
